import React, { useState } from 'react';
import { useTheme } from '@mui/material/styles';

interface ContactFormProps {
  onSent?: () => void;
  onClose?: () => void;
  initialEmail?: string | null;
}

const SupportContactForm: React.FC<ContactFormProps> = ({ onSent, onClose, initialEmail }) => {
  const [contactEmail, setContactEmail] = useState(initialEmail || '');
  const [contactSubject, setContactSubject] = useState('');
  const [contactMessage, setContactMessage] = useState('');
  const [contactStatus, setContactStatus] = useState('');
  const [sending, setSending] = useState(false);
  const theme = useTheme();
  const mode = theme?.palette?.mode || 'light';

  return (
    <form
      className={`contact-form contact-form--${mode}`}
      onSubmit={async (e) => {
        e.preventDefault();
        setContactStatus('');
        const emailRegex = /^\S+@\S+\.\S+$/;
        if (!contactEmail || !contactSubject || !contactMessage) {
          setContactStatus('Please fill all fields: email, subject and message.');
          return;
        }
        if (!emailRegex.test(contactEmail)) {
          setContactStatus('Please enter a valid email address.');
          return;
        }
        setSending(true);
        setContactStatus('Sending...');
        try {
          const base = (process.env.REACT_APP_API_URL as string) || 'http://localhost:8090';
          const resp = await fetch(`${base}/api/mailing/contact`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email: contactEmail, subject: contactSubject, message: contactMessage }),
          });
          const result = await resp.json().catch(() => ({}));
          if (result.success) {
            setContactStatus('Message sent! We will get back to you soon.');
            setContactEmail('');
            setContactSubject('');
            setContactMessage('');
            if (onSent) onSent();
          } else {
            setContactStatus(result.message || 'Failed to send.');
          }
        } catch (err) {
          setContactStatus('Failed to send. Please try again later.');
        } finally {
          setSending(false);
        }
      }}
    >
      <div className="contact-form-header">
        <button type="button" className="contact-close" onClick={() => onClose && onClose()}>×</button>
      </div>
      <div>
        <label htmlFor="contact-email">Your email address</label>
        <input
          id="contact-email"
          className="contact-input"
          type="email"
          value={contactEmail}
          onChange={e => setContactEmail(e.target.value)}
          required
        />
      </div>
      <div>
        <label htmlFor="contact-subject">Subject</label>
        <input
          id="contact-subject"
          className="contact-input"
          type="text"
          value={contactSubject}
          onChange={e => setContactSubject(e.target.value)}
          required
        />
      </div>
      <div>
        <label htmlFor="contact-message">Message</label>
        <textarea
          id="contact-message"
          className="contact-textarea"
          value={contactMessage}
          onChange={e => setContactMessage(e.target.value)}
          required
          rows={4}
        />
      </div>
      <div className="contact-actions">
        <button type="submit" className="btn-primary contact-button" disabled={sending}>
          {sending ? 'Sending...' : 'Send'}
        </button>
        <button type="button" className="btn-secondary contact-close-button" onClick={() => onClose && onClose()}>
          Close
        </button>
      </div>
      {contactStatus && (
        <div className={`contact-status ${contactStatus.toLowerCase().includes('sent') ? 'success' : ''}`}>{contactStatus}</div>
      )}
    </form>
  );
};

export default SupportContactForm;
